import { MutableRefObject, useEffect, useState } from 'react';

interface Props {
  when?: boolean;
  deps?: any[];
  threshold?: number;
  continuous?: boolean;
}

export const useIntersection = (
  ref: MutableRefObject<Element | null>,
  { when = true, deps = [], threshold = 0.01, continuous = false }: Props = {},
) => {
  const [hasIntersected, setHasIntersected] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!when || !ref?.current) {
      return;
    }

    if (!continuous && hasIntersected) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isCurrentlyIntersecting =
            entry.isIntersecting && entry.intersectionRect.height > 0 && entry.intersectionRatio >= threshold;

          if (continuous) {
            setIsIntersecting(isCurrentlyIntersecting);
          }

          if (isCurrentlyIntersecting) {
            setHasIntersected(true);

            if (!continuous) {
              observer.disconnect();
            }
          }
        });
      },
      { threshold },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, when, threshold, continuous, hasIntersected, ...deps]);

  return continuous ? isIntersecting : hasIntersected;
};
